<template>
  <div class="fixed inset-0 flex items-center justify-center z-50">
    <div class="absolute inset-0 bg-black/40" @click="close"></div>
    <div class="relative bg-white rounded-2xl shadow-xl p-6 w-80">
      <div class="flex items-center justify-between mb-5">
        <div class="text-gray-800 font-semibold text-base">Audio Annotation</div>
        <button @click="close" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="flex flex-col items-center gap-3 py-6">
        <div class="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span class="text-sm text-gray-500">Loading audio…</span>
        <span v-if="duration" class="text-xs text-gray-400 tabular-nums">{{ formatTime(duration) }}</span>
      </div>

      <!-- Error state -->
      <div v-else-if="errorMsg" class="text-red-500 text-sm text-center py-4">{{ errorMsg }}</div>

      <!-- Player -->
      <div v-else class="flex flex-col items-center gap-4">
        <!-- Custom controls -->
        <div class="flex items-center gap-4 w-full">
          <button
            @click="togglePlay"
            class="w-12 h-12 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors shrink-0"
          >
            <!-- Play icon -->
            <svg v-if="!isPlaying" width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 4L16 10L6 16V4Z"/>
            </svg>
            <!-- Pause icon -->
            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="4" width="3.5" height="12" rx="1"/>
              <rect x="11.5" y="4" width="3.5" height="12" rx="1"/>
            </svg>
          </button>

          <div class="flex-1 flex flex-col gap-1">
            <!-- Seek bar -->
            <input
              type="range"
              min="0"
              :max="duration || 100"
              :value="currentTime"
              @input="seek"
              class="w-full accent-blue-500 cursor-pointer"
            />
            <!-- Time display -->
            <div class="flex justify-between text-xs text-gray-400 tabular-nums">
              <span>{{ formatTime(currentTime) }}</span>
              <span>{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>

        <div v-if="playbackError" class="text-red-500 text-xs text-center">{{ playbackError }}</div>

        <audio
          ref="audioEl"
          :src="objectUrl"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onMetadata"
          @ended="onEnded"
          class="hidden"
        ></audio>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onBeforeUnmount } from "vue";

export default {
  name: "AudioPlayer",
  props: {
    objectUrl: {
      type: String,
      required: false,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errorMsg: {
      type: String,
      default: "",
    },
    initialDuration: {
      type: Number,
      default: 0,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const audioEl = ref(null);
    const isPlaying = ref(false);
    const currentTime = ref(0);
    const duration = ref(props.initialDuration);
    const playbackError = ref("");

    function togglePlay() {
      if (!audioEl.value) return;
      if (isPlaying.value) {
        audioEl.value.pause();
        isPlaying.value = false;
        return;
      }
      audioEl.value.play().then(() => {
        isPlaying.value = true;
      }).catch((err) => {
        console.error("Audio playback error", err);
        isPlaying.value = false;
        playbackError.value = "Unable to play this recording.";
      });
    }

    function seek(e) {
      if (!audioEl.value) return;
      audioEl.value.currentTime = Number(e.target.value);
    }

    function onTimeUpdate() {
      if (audioEl.value) currentTime.value = audioEl.value.currentTime;
    }

    function onMetadata() {
      if (audioEl.value) duration.value = audioEl.value.duration;
    }

    function onEnded() {
      isPlaying.value = false;
      currentTime.value = 0;
    }

    function formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return "0:00";
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    }

    function close() {
      if (audioEl.value) {
        audioEl.value.pause();
      }
      emit("close");
    }

    onBeforeUnmount(() => {
      if (audioEl.value) audioEl.value.pause();
    });

    return {
      audioEl, isPlaying, currentTime, duration, playbackError,
      togglePlay, seek, onTimeUpdate, onMetadata, onEnded, formatTime, close,
    };
  },
};
</script>
